import { useCallback, useEffect, useRef, useState } from 'react';
import type { UsoDelAlmacenamiento } from '../../persistencia/repositorio';
import { bytes, porcentaje } from '../formato';
import { M } from '../mensajes/es-PE';
import { repositorio } from '../servicios/repositorio';
import { avisar } from '../ui/avisos';
import { FondoDialogo } from '../ui/Dialogo';
import { Icono } from '../ui/Icono';

interface Medicion {
  /** Lo que informa el navegador para todo el sitio (`navigator.storage.estimate`). */
  usado: number | null;
  cuota: number | null;
  persistente: boolean | null;
  propio: UsoDelAlmacenamiento | null;
}

const almacen = (): StorageManager | null =>
  typeof navigator !== 'undefined' && navigator.storage ? navigator.storage : null;

/**
 * Medidor de almacenamiento (paso 1.5, doc. 02 §7): cuánto usa el sitio en este navegador y cuánto
 * le permite usar, el desglose entre proyectos e historial, y la protección contra el borrado
 * automático (`navigator.storage.persist`). Explica los límites del almacenamiento local.
 */
export function MedidorAlmacenamiento({ actualizar = 0 }: { actualizar?: number }) {
  const [m, setM] = useState<Medicion | null>(null);
  const [verLimites, setVerLimites] = useState(false);

  const medir = useCallback(async () => {
    const s = almacen();
    const [estimacion, persistente, propio] = await Promise.all([
      s?.estimate ? s.estimate().catch(() => null) : null,
      s?.persisted ? s.persisted().catch(() => null) : null,
      repositorio()
        .uso()
        .catch(() => null),
    ]);
    setM({
      usado: estimacion?.usage ?? null,
      cuota: estimacion?.quota ?? null,
      persistente,
      propio,
    });
  }, []);

  useEffect(() => {
    void medir();
  }, [medir, actualizar]);

  const proteger = async () => {
    const s = almacen();
    const concedido = s?.persist ? await s.persist().catch(() => false) : false;
    avisar(
      concedido ? M.almacenamiento.protegidoAhora : M.almacenamiento.rechazado,
      concedido ? 'exito' : 'advertencia',
    );
    void medir();
  };

  const fraccion = m?.usado != null && m.cuota ? m.usado / m.cuota : null;

  return (
    <section
      className="panel medidor-almacenamiento"
      aria-labelledby="titulo-almacenamiento"
      data-testid="medidor-almacenamiento"
      data-usado={m?.usado ?? ''}
    >
      <h2 id="titulo-almacenamiento" className="titulo-seccion">
        {M.almacenamiento.titulo}
      </h2>
      {m === null ? (
        <p className="atenuado" role="status">
          {M.app.cargando}
        </p>
      ) : m.usado === null || m.cuota === null ? (
        <p className="secundario">{M.almacenamiento.noDisponible}</p>
      ) : (
        <>
          <div
            className="barra-almacenamiento"
            role="meter"
            aria-label={M.almacenamiento.titulo}
            aria-valuemin={0}
            aria-valuemax={m.cuota}
            aria-valuenow={m.usado}
            aria-valuetext={M.almacenamiento.uso(bytes(m.usado), bytes(m.cuota))}
          >
            <span style={{ width: `${Math.max(1, Math.min(100, (fraccion ?? 0) * 100))}%` }} />
          </div>
          <p className="num" data-testid="uso-almacenamiento">
            {M.almacenamiento.uso(bytes(m.usado), bytes(m.cuota))}
            {fraccion !== null ? (
              <span className="atenuado">
                {' '}
                (
                {fraccion < 0.0001
                  ? `< ${porcentaje(0.0001, 2)}`
                  : porcentaje(fraccion, fraccion < 0.01 ? 2 : 1)}
                )
              </span>
            ) : null}
          </p>
        </>
      )}
      {m?.propio ? (
        <p className="secundario num">
          {M.almacenamiento.desglose(bytes(m.propio.proyectos), bytes(m.propio.versiones))}
        </p>
      ) : null}
      {m && m.persistente !== null ? (
        <p className={`proteccion ${m.persistente ? 'si' : 'no'}`} data-testid="proteccion">
          <Icono nombre={m.persistente ? 'ok' : 'alerta'} tamano={16} />
          <span>{m.persistente ? M.almacenamiento.protegido : M.almacenamiento.sinProteger}</span>
          {!m.persistente ? (
            <button
              type="button"
              className="boton chico"
              onClick={() => void proteger()}
              title={M.almacenamiento.protegerAyuda}
            >
              {M.almacenamiento.proteger}
            </button>
          ) : null}
        </p>
      ) : null}
      <button type="button" className="boton-enlace" onClick={() => setVerLimites(true)}>
        <Icono nombre="info" tamano={16} />
        {M.almacenamiento.limites}
      </button>
      {verLimites ? <DialogoLimites alCerrar={() => setVerLimites(false)} /> : null}
    </section>
  );
}

/** «¿Dónde viven tus datos?»: límites del almacenamiento local frente a uno remoto (prompt §10). */
function DialogoLimites({ alCerrar }: { alCerrar: () => void }) {
  const cerrar = useRef<HTMLButtonElement>(null);
  useEffect(() => cerrar.current?.focus(), []);
  const a = M.almacenamiento;
  return (
    <FondoDialogo alCerrar={alCerrar}>
      <div
        className="panel dialogo dialogo-limites"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-limites"
      >
        <h2 id="titulo-limites">{a.limitesTitulo}</h2>
        <h3>{a.limitesLocal}</h3>
        <ul>
          {a.limitesLocalLista.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <h3>{a.limitesRemoto}</h3>
        <ul>
          {a.limitesRemotoLista.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <div className="acciones">
          <button ref={cerrar} type="button" className="boton" onClick={alCerrar}>
            {M.app.cerrar}
          </button>
        </div>
      </div>
    </FondoDialogo>
  );
}
